import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { InputSearchDatePeriod } from '../../../shared/components/input/input.search.datePeriod';
import { Filter } from '../../../shared/filter/filter';
import { FLASH_CALL_CHANNEL_FILTER } from '../../../shared/models/flash-call.model';
import { FLASH_CALL_TEST_STATUS, LIVE_NUMBER_TEST_STATUSES, MESSAGE_STATES } from '../../../shared/models/test-group.model';
import { AuthUser, Role } from '../../../shared/models/user.model';
import { CustomUtils } from '../../../shared/services/custom-utils';
import { ExportService } from '../../../shared/services/export.service';
import { LiveNumberTestingService, SearchParams } from '../../../shared/services/live-number-testing.service';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { UsersService } from '../../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../../shared/services/validation.service';
import { UsersSelectComponent } from '../../../users/users-select/users-select.component';
import { DestinationsSelectComponent } from '../../destinations-select/destinations-select.component';
import { RoutesSelectComponent } from '../../routes-select/routes-select.component';
import { UdhTlvInputComponent } from '../../udh-tlv-input/udh-tlv-input.component';

@Component({
  standalone: false,
  selector: 'app-lnt-search-form',
  templateUrl: 'lnt-search-form.component.html',
  styleUrls: ['lnt-search-form.component.scss'],
})
export class LntSearchFormComponent implements OnInit, AfterViewInit {
  private static readonly IDS_MAX_COUNT = 100;
  private static readonly ADMIN_IDS_MAX_COUNT = 1000;
  private static readonly ID_MAX_LENGTH = 10;
  usersModal: DialogRef;

  @Output() onSubmit = new EventEmitter();
  @Output() onHide = new EventEmitter();

  @ViewChild(InputSearchDatePeriod) datePeriod: InputSearchDatePeriod;
  @ViewChildren(InputSearchDatePeriod) childDatePeriods: QueryList<InputSearchDatePeriod>;
  @ViewChild('filterTopBar', { static: false }) filterTopBar: ElementRef;
  @ViewChild('tlvListComponent', { read: UdhTlvInputComponent, static: false }) tlvListComponent: UdhTlvInputComponent;
  @ViewChild('exportModalTpl', { read: TemplateRef, static: false }) exportModalTpl: any;
  @ViewChild('destinationContainer', { read: ViewContainerRef }) destinationContainer: ViewContainerRef;
  @ViewChild('supplierContainer', { read: ViewContainerRef }) supplierContainer: ViewContainerRef;
  @ViewChild('userContainer', { read: ViewContainerRef }) userContainer: ViewContainerRef;
  private countries: DestinationsSelectComponent;
  private destinationFilterComponentReference: ComponentRef<DestinationsSelectComponent>;
  private connections: RoutesSelectComponent;
  private supplierFilterComponentReference: ComponentRef<RoutesSelectComponent>;
  private users: UsersSelectComponent;
  private userFilterComponentReference: ComponentRef<UsersSelectComponent>;

  usersCollection = [];
  showUsers = false;
  onlyMyTests = false;
  userId: number;
  saveUserIds = true;
  filters: Filter[] = [
    { filterKey: 'date', title: 'Date', show: true },
    { filterKey: 'destinations', title: 'Destinations', show: true },
    { filterKey: 'suppliers', title: 'Suppliers', show: false },
    { filterKey: 'users', title: 'Users', show: false },
    { filterKey: 'statuses', title: 'Status', show: true },
    { filterKey: 'searchById', title: 'ID', show: false },
    { filterKey: 'searchByPhoneNumbers', title: 'Phone', show: false },
    { filterKey: 'searchByText', title: 'Text', show: false },
    { filterKey: 'searchByComment', title: 'Comment', show: false },
    { filterKey: 'commentDate', title: 'Comment Date', show: false },
    { filterKey: 'searchByTestIdText', title: 'Test ID Text', show: false },
    { filterKey: 'searchBySenderId', title: 'Sender ID', show: false },
    { filterKey: 'tlv', title: 'TLV tag and value', show: false },
    { filterKey: 'testGroupIds', title: 'Test group ID', show: false },
    { filterKey: 'taskIds', title: 'Scheduled task ID', show: false },
    { filterKey: 'channels', title: 'Delivered as (channel)', show: false },
  ];

  model = {
    ids: '',
    telqIds: '',
    dlr_statuses: [],
    receipt_statuses: [],
    user_ids: [],
    sender: '',
    from: null,
    channels: [],
    to: null,
    commentText: null,
    text: null,
    commentDateFrom: null,
    commentDateTo: null,
    phoneNumbers: '',
    testGroupIds: '',
    taskIds: null,
    showOnlyScheduledTests: false,
    destinations: [],
    sessionSupplierIds: [],
    tlv: {
      tagHex: null,
      valueHex: null,
    },
  };
  dlrStatuses = [];
  receiptStatuses = [];
  showFlashCallFilters = false;
  readonly flashCallChannel = FLASH_CALL_CHANNEL_FILTER;

  form: FormGroup;

  user: AuthUser;

  loading = false;

  tlvValid = true;
  exportSize: string | number = 'Custom';
  customExportSize = 1000;
  exportSizes = ['Custom', 'All'];
  exportData: any = {};
  exportSpinner = false;
  isBeingDragged = false;

  private getIdsMaxCount(): number {
    return this.user?.role === Role.ADMIN ? LntSearchFormComponent.ADMIN_IDS_MAX_COUNT : LntSearchFormComponent.IDS_MAX_COUNT;
  }

  private updateIdsValidators(): void {
    this.form.controls.ids.setValidators(
      Vld.compose([Vld.digitsAndSpaces(true), Vld.idsCount(this.getIdsMaxCount()), Vld.singleIdMaxLength(LntSearchFormComponent.ID_MAX_LENGTH)])
    );
    this.form.controls.ids.updateValueAndValidity({ emitEvent: false });
  }

  constructor(
    public userService: UsersService,
    public modal: ModalService,
    public formBuilder: FormBuilder,
    public validationService: ValidationService,
    public localStorage: LocalStorageService,
    public exportService: ExportService,
    public notificationService: NotificationService,
    private liveNumberTestingService: LiveNumberTestingService
  ) {
    MESSAGE_STATES.forEach(_ => this.dlrStatuses.push(_));
    LIVE_NUMBER_TEST_STATUSES.forEach(_ => this.receiptStatuses.push(_));

    this.validationService = validationService;

    this.form = formBuilder.group({
      ids: [
        '',
        Vld.compose([
          Vld.digitsAndSpaces(true),
          Vld.idsCount(LntSearchFormComponent.IDS_MAX_COUNT),
          Vld.singleIdMaxLength(LntSearchFormComponent.ID_MAX_LENGTH),
        ]),
      ],
      telqIds: ['', Vld.compose([Vld.maxLength(255)])],
      testGroupIds: ['', Vld.compose([Vld.digitsAndSpaces(true), Vld.maxLength(255)])],
      taskIds: ['', Vld.compose([Vld.digitsAndSpaces(true), Vld.maxLength(255)])],
      phoneNumbers: ['', Vld.compose([Vld.maxLength(255)])],
      tlvTag: ['', Vld.compose([UdhTlvInputComponent.createTagValidator(2, false), Vld.hex(true)])],
      tlvValue: ['', Vld.compose([UdhTlvInputComponent.createValueValidator(false), Vld.hex(true)])],
    });
    this.form.statusChanges.subscribe(_ => {
      ValidationService.getInvalidControls(this.form);
    });
  }

  ngOnInit() {
    this.userService.getAuthUser().then(user => {
      this.user = user;
      this.updateIdsValidators();
      const isAdmin = user.role === Role.ADMIN;
      const isMain = user.role === Role.MAIN;
      const isDeputy = user.role === Role.DEPUTY;
      this.showUsers = isAdmin || isMain || isDeputy || user.showAllResults;
      const existingFilters = this.localStorage.get(`lnt_${this.user.id}_filter_state`) || [];
      if (existingFilters.length === this.filters.length) {
        this.filters = existingFilters;
      } else {
        existingFilters.forEach(ef => {
          this.filters
            .filter(_ => _.filterKey === ef.filterKey)
            .forEach(f => {
              f.show = ef.show;
            });
        });
      }
      this.filters = this.filters.filter(f => {
        if (!this.showUsers && f.filterKey === 'users') {
          return false;
        }
        return true;
      });
      this.update();
      this.loadFlashCallFilters(isAdmin);
    });
  }

  // Admins need the filters for every account's tests, so they follow the feature switch rather than their own access.
  private loadFlashCallFilters(isAdmin: boolean): void {
    this.userService.getFlashCallAccess().subscribe(access => {
      if (isAdmin ? access.featureEnabled : access.enabled) {
        this.enableFlashCallFilters();
      } else {
        this.removeSavedFlashCallFilters();
      }
    });
  }

  private enableFlashCallFilters(): void {
    if (this.showFlashCallFilters) {
      return;
    }
    this.showFlashCallFilters = true;
    const savedSearch = this.localStorage.get(`lnt_user_search_${this.user.id}_search`);
    const selected = !!savedSearch?.testStatuses?.includes(FLASH_CALL_TEST_STATUS.id);
    const senderReplacedIndex = this.receiptStatuses.findIndex(status => status.id === 'SENDER_REPLACED');
    this.receiptStatuses.splice(senderReplacedIndex + 1, 0, { ...FLASH_CALL_TEST_STATUS, isNew: true, selected });
  }

  // Without access the flash call options are hidden, so a saved flash call filter could not be seen or cleared anymore.
  private removeSavedFlashCallFilters(): void {
    const savedSearch = this.localStorage.get(`lnt_user_search_${this.user.id}_search`);
    const hasFlashCallFilters =
      savedSearch?.channels?.includes(FLASH_CALL_CHANNEL_FILTER) || savedSearch?.testStatuses?.includes(FLASH_CALL_TEST_STATUS.id);
    if (!hasFlashCallFilters) {
      return;
    }
    this.model.channels = this.model.channels.filter(channel => channel !== FLASH_CALL_CHANNEL_FILTER);
    this.onSubmitClick();
  }

  ngAfterViewInit() {
    this.countries?.reset();
    this.connections?.reset();
    if (this.childDatePeriods.length > 0) {
      const commentDateComponent = this.childDatePeriods.filter(child => child.type === 'commentDate')[0];
      if (commentDateComponent) commentDateComponent.reset();
    }
  }

  onChangeTlv() {
    const tag = this.model.tlv.tagHex;
    const value = this.model.tlv.valueHex;
    this.tlvValid = (!tag && !value) || (tag && value);
  }

  onChangeDate(dates) {
    if (dates instanceof Event || typeof dates === 'string') {
      return;
    }
    if (dates?.startStr && dates?.endStr) {
      this.model.from = dates.startStr + '.000Z';
      this.model.to = dates.endStr + '.999Z';
      this.onSubmitClick();
    } else {
      this.model.from = null;
      this.model.to = null;
    }
  }

  shouldAddFilterBeShown(): boolean {
    const hiddenFilters = this.filters.filter(_ => _.show === false);
    return hiddenFilters.length > 0;
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.filters, event.previousIndex, event.currentIndex);
    this.localStorage.set(`lnt_${this.user.id}_filter_state`, this.filters);
  }

  deleteItem(event: CdkDragDrop<string[]>) {
    const indexToHide = event.previousIndex;
    this.filters[indexToHide].show = false;
    this.onClear(this.filters[indexToHide].filterKey);
    this.localStorage.set(`lnt_${this.user.id}_filter_state`, this.filters);
  }

  update() {
    this.loading = true;
    const savedUserSearchDetails = this.localStorage.get(`lnt_user_search_${this.user.id}_search`);
    if (savedUserSearchDetails) {
      this.saveUserIds = true;
      this.populateSavedDetails(savedUserSearchDetails);
    } else {
      this.resetAll();
    }
    this.loading = false;
  }

  populateSavedDetails(savedModel): void {
    this.model.telqIds = savedModel.telqIds.join(', ');
    if (savedModel.ids && savedModel.ids.length) {
      this.model.ids = savedModel.ids.join(' ');
    }
    if (savedModel.testGroupIds && savedModel.testGroupIds.length) {
      this.model.testGroupIds = savedModel.testGroupIds.join(' ');
    }
    if (savedModel.taskIds && savedModel.taskIds.length) {
      this.model.taskIds = savedModel.taskIds.join(' ');
    }
    this.model.phoneNumbers = savedModel.destinations
      .filter(_ => _.manualNumber)
      .map(_ => _.phone)
      .join('\n');
    this.model.destinations = savedModel.destinations.filter(_ => !_.manualNumber);
    this.model.sessionSupplierIds = savedModel.sessionSupplierIds;
    this.model.sender = savedModel.senders[0];
    this.model.from = savedModel.createdAtFrom;
    this.model.to = savedModel.createdAtTo;
    this.usersCollection = savedModel.usersCollection;
    // Check if Show only my tests is enabled... This means usersCollection has only one id of current logged in user ...
    if (savedModel.userIds[0] === this.user.id) {
      this.onlyMyTests = true;
    }
    this.dlrStatuses.map(dlrstatus => {
      if (savedModel.messageStates.includes(dlrstatus.id)) {
        dlrstatus.selected = true;
      }
    });
    this.receiptStatuses.map(recieptstatus => {
      if (savedModel.testStatuses.includes(recieptstatus.id)) {
        recieptstatus.selected = true;
      }
    });
    this.model.channels = savedModel.channels ?? [];

    this.model.commentText = savedModel.commentText;
    this.model.commentDateFrom = savedModel.commentDateFrom;
    this.model.commentDateTo = savedModel.commentDateTo;
    if (savedModel.text) {
      this.model.text = savedModel.text;
    }

    setTimeout(() => {
      if (this.datePeriod) {
        this.datePeriod.start = Date.parse(this.model.from) / 1000;
        this.datePeriod.end = Date.parse(this.model.to) / 1000;
        this.datePeriod.updateValue();
      }
      if (this.childDatePeriods.length > 0) {
        const commentDateComponent = this.childDatePeriods.filter(child => child.type === 'commentDate')[0];
        if (commentDateComponent) {
          commentDateComponent.start = Date.parse(this.model.commentDateFrom) / 1000;
          commentDateComponent.end = Date.parse(this.model.commentDateTo) / 1000;
          commentDateComponent.updateValue();
        }
      }
    }, 200);

    if (savedModel.tlv) {
      this.model.tlv = savedModel.tlv;
    }
  }

  getPopulatedModel(): any {
    const model: SearchParams = {};
    model.sessionSupplierIds = this.model.sessionSupplierIds;
    const rawDestinations = this.model.destinations;
    const filterMccMncPortedMncMap = this.countries ? this.countries.filterMccMncPortedMncMap : new Map();
    const destinations: any[] = [];
    rawDestinations.forEach(dest => {
      if (dest.mcc && dest.mnc) {
        const key = `${dest.mcc}_${dest.mnc}`;
        const entry = filterMccMncPortedMncMap.get(key);
        if (entry && Array.isArray(entry.specificMncs) && entry.specificMncs.length > 0) {
          entry.specificMncs.forEach(specificMnc => {
            destinations.push({
              mcc: dest.mcc,
              mnc: dest.mnc,
              originalMnc: specificMnc,
            });
          });
        } else {
          destinations.push(dest);
        }
      } else {
        destinations.push(dest);
      }
    });
    model.destinations = destinations;

    if (this.model.phoneNumbers) {
      this.model.phoneNumbers.split('\n').map(_ => {
        const phone = _.trim();
        if (phone.length > 0) {
          model.destinations.push({ phone: phone, manualNumber: true });
        }
      });
    }
    model.senders = [];
    if (this.model.sender) {
      model.senders.push(this.model.sender.trim());
    }
    model.messageStates = this.dlrStatuses.filter(_ => _.selected).map(_ => _.id);
    model.testStatuses = this.receiptStatuses.filter(_ => _.selected).map(_ => _.id);

    model.ids = [];
    if (this.model.ids) {
      model.ids = CustomUtils.splitByWhitespace(this.model.ids);
    }
    model.telqIds = [];
    if (this.model.telqIds) {
      model.telqIds = this.model.telqIds
        .split(',')
        .map(_ => _.trim())
        .filter(_ => _);
    }
    model.testGroupIds = [];
    if (this.model.testGroupIds) {
      model.testGroupIds = CustomUtils.splitByWhitespace(this.model.testGroupIds);
    }
    model.taskIds = [];
    if (this.model.taskIds) {
      model.taskIds = CustomUtils.splitByWhitespace(this.model.taskIds).map(id => parseInt(id, 10));
    }
    if (this.model.from) {
      model.createdAtFrom = this.model.from;
    }
    if (this.model.to) {
      model.createdAtTo = this.model.to;
    }
    model.userIds = this.onlyMyTests ? [this.user.id] : this.usersCollection.map(u => u.id);
    model.channels = this.model.channels;
    model.commentText = this.model.commentText;
    model.commentDateFrom = this.model.commentDateFrom;
    model.commentDateTo = this.model.commentDateTo;
    let text = null;
    if (typeof this.model.text === 'string') {
      text = this.model.text.trim();
    }
    model.text = text ? text : null;
    if (this.model.tlv) {
      model.tlv = {
        tagHex: this.model.tlv.tagHex ? this.model.tlv.tagHex : null,
        valueHex: this.model.tlv.valueHex ? this.model.tlv.valueHex : null,
      };
      if (!model.tlv.tagHex && !model.tlv.valueHex) {
        delete model.tlv;
      }
    }
    model.showOnlyScheduledTests = this.model.showOnlyScheduledTests;
    return model;
  }

  checkFilterCondition(filterKey: string): void {
    if (filterKey === 'searchByPhoneNumbers' && this.countries?.selectedCount > 0) {
      this.countries?.reset();
      this.notificationService.info(
        'Destination and phone number filter cannot be used together. Destination filter has been cleared.',
        'Destination filter updated'
      );
    }
    const populatedModel: SearchParams = this.getPopulatedModel();
    const isNonElasticSearchFilterActive = this.isNonElasticSearchFilterActive();
    if (this.liveNumberTestingService.ELASTIC_FILTERS.includes(filterKey)) {
      if (isNonElasticSearchFilterActive) {
        this.clearNonElasticSearchFilters();
        this.notificationService.info('This filter combination cannot be used', 'Some filters are updated');
      }
    } else {
      const isElasticSearchFilterActive = this.liveNumberTestingService.shouldRunOnElasticSearch(populatedModel);
      if (isElasticSearchFilterActive) {
        this.clearElasticSearchFilters();
        this.notificationService.info('This filter combination cannot be used', 'Some filters are updated');
      }
    }
  }

  isNonElasticSearchFilterActive(): boolean {
    for (let i = 0; i < this.filters.length; i++) {
      const f = this.filters[i];
      if (!this.liveNumberTestingService.ELASTIC_FILTERS.includes(f.filterKey)) {
        const isFActive = this.isFilterActive(f.filterKey);
        if (isFActive) {
          return true;
        }
      }
    }
    return false;
  }

  clearElasticSearchFilters(): void {
    this.model.commentDateFrom = null;
    this.model.commentDateTo = null;
    this.model.commentText = null;
    this.model.text = null;
    this.childDatePeriods?.map(child => child.reset());
  }

  clearNonElasticSearchFilters(): void {
    this.model.telqIds = '';
    this.model.ids = '';
    this.model.sender = '';
    this.model.from = '';
    this.model.to = '';
    this.model.tlv.tagHex = null;
    this.model.tlv.valueHex = null;
    this.model.testGroupIds = '';
    this.model.taskIds = '';
    this.model.channels = [];
    if (this.datePeriod) {
      this.datePeriod.start = Date.parse(this.model.from) / 1000;
      this.datePeriod.end = Date.parse(this.model.to) / 1000;
      this.datePeriod.updateValue();
    }
    this.model.phoneNumbers = '';
    this.model.destinations = [];
    this.model.sessionSupplierIds = [];
    this.receiptStatuses.map(_ => (_.selected = false));
    this.dlrStatuses.map(_ => (_.selected = false));
    this.countries?.reset();
    this.connections?.reset();
    this.usersCollection = this.onlyMyTests ? this.usersCollection : [];
    this.users?.onClearAll();
  }

  resetAll(): void {
    this.model.telqIds = '';
    this.model.ids = '';
    this.model.sender = '';
    this.model.from = '';
    this.model.to = '';
    this.model.commentDateFrom = null;
    this.model.commentDateTo = null;
    this.model.commentText = null;
    this.model.text = null;
    this.model.tlv.tagHex = null;
    this.model.tlv.valueHex = null;
    this.model.testGroupIds = '';
    this.model.showOnlyScheduledTests = false;
    this.model.taskIds = '';
    this.model.channels = [];
    this.childDatePeriods?.map(child => child.reset());
    if (this.datePeriod) {
      this.datePeriod.start = Date.parse(this.model.from) / 1000;
      this.datePeriod.end = Date.parse(this.model.to) / 1000;
      this.datePeriod.updateValue();
    }
    this.model.phoneNumbers = '';
    this.model.destinations = [];
    this.model.sessionSupplierIds = [];
    this.receiptStatuses.map(_ => (_.selected = false));
    this.dlrStatuses.map(_ => (_.selected = false));
    this.countries?.reset();
    this.connections?.reset();
    this.usersCollection = this.onlyMyTests ? this.usersCollection : [];
    this.users?.onClearAll();
    // this.saveUserIds = false;
  }

  onSubmitClick(filterKey: string = null) {
    this.checkFilterCondition(filterKey);
    const searchDetails: SearchParams = this.getPopulatedModel();
    if (this.saveUserIds) {
      searchDetails.usersCollection = this.usersCollection;
      this.localStorage.set(`lnt_user_search_${this.user.id}_search`, searchDetails);
    } else {
      this.localStorage.remove(`lnt_user_search_${this.user.id}_search`);
    }
    this.onSubmit.emit(searchDetails);
    this.handleFilterDropdown(filterKey);
  }

  handleFilterDropdown(filterKey: string = null) {
    const closeDropDownForTheseFilters = [
      'date',
      'searchById',
      'searchByPhoneNumbers',
      'searchByText',
      'searchByComment',
      'commentDate',
      'searchByTestIdText',
      'searchBySenderId',
      'tlv',
    ];
    if (filterKey && closeDropDownForTheseFilters.includes(filterKey)) {
      this.filterTopBar.nativeElement.click();
    }
  }

  onClickHide() {
    const searchDetails: SearchParams = this.getPopulatedModel();
    searchDetails.usersCollection = this.usersCollection;
    if (this.saveUserIds) {
      this.localStorage.set(`lnt_user_search_${this.user.id}_search`, searchDetails);
    } else {
      this.localStorage.remove(`lnt_user_search_${this.user.id}_search`);
    }
    this.onHide.emit(searchDetails);
  }

  onSelectUser(user) {
    this.usersCollection.push(user);
    this.onlyMyTests = false;
    this.saveUserSearch();
    this.onSubmitClick();
  }

  onUnSelectUser(user, event: Event = null) {
    if (event) {
      event.stopPropagation();
    }
    this.usersCollection = this.usersCollection.filter(u => user.id !== u.id);
    this.saveUserSearch();
    this.onSubmitClick();
  }

  showOnlyScheduledTestsOnChange(): void {
    this.model.showOnlyScheduledTests = !this.model.showOnlyScheduledTests;
    this.onSubmitClick();
  }

  onChangeOnlyMyTests() {
    this.onlyMyTests = !this.onlyMyTests;
    if (this.onlyMyTests) {
      this.usersCollection = [];
      this.users?.onClearAll();
      this.onSubmitClick();
    } else {
      this.onUnSelectUser(this.user);
    }
    this.saveUserSearch();
  }

  onChangeSaveUserIds() {
    this.saveUserIds = !this.saveUserIds;
    if (this.saveUserIds) {
      const searchDetails = this.getPopulatedModel();
      searchDetails.usersCollection = this.usersCollection;
      this.localStorage.set(`lnt_user_search_${this.user.id}_search`, searchDetails);
    } else {
      this.localStorage.remove(`lnt_user_search_${this.user.id}_search`);
    }
    this.saveUserSearch();
  }

  saveUserSearch() {
    const ids = this.usersCollection.map(u => u.id);
    if (ids.length && this.saveUserIds) {
      this.localStorage.set('lnt_user_search_' + this.user.id, ids);
    } else {
      this.localStorage.remove('lnt_user_search_' + this.user.id);
    }
  }

  static getSaveUserIds(localStorage: LocalStorageService, user) {
    return localStorage.get('lnt_user_search_' + user.id, []);
  }

  onChangeCommentDate(dates): void {
    if (dates instanceof Event || typeof dates === 'string') {
      return;
    }
    if (dates?.startStr && dates?.endStr) {
      this.model.commentDateFrom = dates.startStr + '.000Z';
      this.model.commentDateTo = dates.endStr + '.999Z';
      this.onSubmitClick('commentDate');
    } else {
      this.model.commentDateFrom = null;
      this.model.commentDateTo = null;
    }
  }

  onClearAllStatus(): void {
    this.dlrStatuses.forEach(ds => (ds.selected = false));
    this.receiptStatuses.forEach(rs => (rs.selected = false));
    this.onSubmitClick();
  }

  statusChange(event: any, status: any): void {
    event.stopPropagation();
    status.selected = !status.selected;
    this.onSubmitClick();
  }

  onAdditionalFilterClick(filter): void {
    filter.show = !filter.show;
    this.localStorage.set(`lnt_${this.user.id}_filter_state`, this.filters);
  }

  onClearAllFilter(): void {
    this.localStorage.set(`lnt_${this.user.id}_filter_state`, this.filters);
    this.resetAll();
    this.onClickHide();
  }

  onClear(filterKey: string) {
    switch (filterKey) {
      case 'searchById':
        this.model.ids = '';
        break;
      case 'searchByPhoneNumbers':
        this.model.phoneNumbers = '';
        break;
      case 'searchByText':
        this.model.text = '';
        break;
      case 'searchByComment':
        this.model.commentText = '';
        break;
      case 'searchByTestIdText':
        this.model.telqIds = '';
        break;
      case 'searchBySenderId':
        this.model.sender = '';
        break;
      case 'date':
        if (this.datePeriod) {
          this.datePeriod.model = '';
          this.datePeriod.control.patchValue('');
          this.datePeriod.reset();
        }
        this.onChangeDate(null);
        this.handleFilterDropdown('date');
        break;
      case 'users':
        this.usersCollection = [];
        break;
      case 'statuses':
        this.dlrStatuses.forEach(ds => (ds.selected = false));
        this.receiptStatuses.forEach(rs => (rs.selected = false));
        break;
      case 'tlv':
        this.model.tlv.tagHex = '';
        this.model.tlv.valueHex = '';
        break;
      case 'commentDate':
        if (this.childDatePeriods.length > 0) {
          const commentDateComponent = this.childDatePeriods.filter(child => child.type === 'commentDate')[0];
          commentDateComponent.reset();
          this.onChangeCommentDate(null);
          this.handleFilterDropdown('commentDate');
        }
        break;
      case 'testGroupIds':
        this.model.testGroupIds = '';
        break;
      case 'taskIds':
        this.model.taskIds = '';
        this.model.showOnlyScheduledTests = false;
        break;
      case 'channels':
        this.model.channels = [];
        break;
    }
    this.onSubmitClick(filterKey);
  }

  isFilterActive(filterKey: string): boolean {
    switch (filterKey) {
      case 'destinations':
        return this.model.destinations.length > 0;
      case 'suppliers':
        return this.model.sessionSupplierIds.length > 0;
      case 'searchById':
        return this.model.ids?.length > 0;
      case 'searchByPhoneNumbers':
        return this.model.phoneNumbers?.length > 0;
      case 'searchByText':
        return this.model.text?.length > 0;
      case 'searchByComment':
        return this.model.commentText?.length > 0;
      case 'searchByTestIdText':
        return this.model.telqIds?.length > 0;
      case 'searchBySenderId':
        return this.model.sender?.length > 0;
      case 'date':
        return this.model.from || this.model.to;
      case 'users':
        return this.usersCollection.length > 0;
      case 'statuses':
        return this.dlrStatuses.some(ds => ds.selected) || this.receiptStatuses.some(rs => rs.selected);
      case 'tlv':
        return this.model.tlv.tagHex || this.model.tlv.tagHex;
      case 'commentDate':
        return this.model.commentDateFrom || this.model.commentDateTo;
      case 'testGroupIds':
        return this.model.testGroupIds?.length > 0;
      case 'taskIds':
        return this.model.taskIds?.length > 0 || this.model.showOnlyScheduledTests;
      case 'channels':
        return this.model.channels?.length > 0;
      default:
        return false;
    }
  }

  getPeekForSelectedValues(filter): number | string | null {
    switch (filter.filterKey) {
      case 'destinations':
        const selectedCountriesCount = this.model.destinations.length;
        return selectedCountriesCount > 0 ? selectedCountriesCount : null;
      case 'suppliers':
        const selectedSuppliersCount = this.model.sessionSupplierIds.length;
        return selectedSuppliersCount > 0 ? selectedSuppliersCount : null;
      case 'users':
        return this.usersCollection.length;
      case 'statuses':
        const count = this.dlrStatuses.filter(ds => ds.selected).length + this.receiptStatuses.filter(rs => rs.selected).length;
        return count;
      case 'searchByPhoneNumbers':
        const phoneNumbers = this.model.phoneNumbers.split('\n').filter(_ => _.trim().length > 0);
        return phoneNumbers?.length;
      default:
        return null;
    }
  }

  onClickExport() {
    this.exportSize = 'Custom';
    this.exportSpinner = false;
    this.exportData = {};
    this.modal.alert().dialogClass('modal-dialog small-modal').component(this.exportModalTpl).open();
  }

  export(limit) {
    if (limit === 'Custom') limit = this.customExportSize;
    this.exportSpinner = true;
    let params = this.getPopulatedModel();
    const ids = LntSearchFormComponent.getSaveUserIds(this.localStorage, this.user);
    if (!params && ids.length) {
      params = { userIds: ids };
    }

    this.exportService
      .export('lnt', limit === 'All' ? null : limit, params ? params : {})
      .then(exportData => {
        this.exportData = exportData;
        this.exportSpinner = false;
      })
      .catch(error => {
        this.notificationService.error({
          title: 'Live number testing',
          message: 'An error occurred while export tests',
          serviceName: 'NTC',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
        this.exportSpinner = false;
      });
  }

  onFilterClick(filter: Filter): void {
    switch (filter.filterKey) {
      case 'destinations':
        if (!this.destinationFilterComponentReference) {
          this.destinationFilterComponentReference = this.destinationContainer.createComponent(DestinationsSelectComponent);
          this.countries = this.destinationFilterComponentReference.instance;
          this.countries.recent = true;
          this.countries.onChange.subscribe(_ => {
            this.model.destinations = this.countries.getSelectedModels().map(_ => {
              return { mcc: _.mcc, mnc: _.mnc };
            });
            if (this.model.phoneNumbers?.length > 0) {
              this.model.phoneNumbers = '';
              this.notificationService.info(
                'Destination and phone number filter cannot be used together. Phone number filter has been cleared.',
                'Phone filter updated'
              );
            }
            this.onSubmitClick();
          });
        }
        break;
      case 'suppliers':
        if (!this.supplierFilterComponentReference) {
          this.supplierFilterComponentReference = this.supplierContainer.createComponent(RoutesSelectComponent);
          this.connections = this.supplierFilterComponentReference.instance;
          this.connections.myConnections = true;
          this.connections.isFilter = true;
          this.connections.onFilterChange.subscribe(_ => {
            this.model.sessionSupplierIds = this.connections.getSelectedModels().map(_ => {
              return { supplierId: _.supplierId, sessionId: _.sessionId };
            });
            this.onClickHide();
          });
          this.connections.onChange.subscribe(_ => {
            this.model.sessionSupplierIds = this.connections.getSelectedModels().map(_ => {
              return { supplierId: _.supplierId, sessionId: _.sessionId };
            });
            this.onSubmitClick();
          });
        }
        break;
      case 'users':
        if (!this.userFilterComponentReference) {
          this.userFilterComponentReference = this.userContainer.createComponent(UsersSelectComponent);
          this.users = this.userFilterComponentReference.instance;
          this.users.multiple = true;
          this.users.clear.subscribe(_ => this.onClear(_));
          this.users.selectUser.subscribe(user => this.onSelectUser(user));
          this.users.unSelectUser.subscribe(user => this.onUnSelectUser(user));
        }
        break;
      default:
        break;
    }
  }

  onChannelChange(channel: string): void {
    if (this.model.channels) {
      if (this.model.channels.includes(channel)) {
        this.model.channels = this.model.channels.filter(c => c !== channel);
      } else {
        this.model.channels.push(channel);
      }
      this.onSubmitClick();
    }
  }

  onClearAllChannel(): void {
    this.model.channels = [];
    this.onSubmitClick();
  }

  getIconForStatus(statusID: string): string {
    return CustomUtils.getIconForStatus(statusID);
  }
}
